import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Badge, Button, Card, Col, Dropdown, Form, InputGroup, Modal, Nav, OverlayTrigger, Row, Spinner, Tab, Tooltip } from "react-bootstrap";
import Select from "react-select";
import { useInfiniteQuery, useQueryClient } from "react-query";
import Icon from "@mdi/react";
import { mdiBellOutline, mdiCalendarCheckOutline, mdiChevronLeft, mdiPlus, mdiPulse, mdiDeleteOutline, mdiCardRemoveOutline } from "@mdi/js";

import {
	useGetGruposListar,
	useGetVariaveisListar,
	useGetMonitoresMonitor,
	useGetMonitoresTasksTask,
	usePostMonitoresCadastrar,
	usePostMonitoresAtualizar,
	getGetMonitoresMonitorQueryKey,
	useGetMonitoresTasksListar,
	getGetMonitoresTasksListarQueryKey,
	getMonitoresTasksListar,
} from "../../config/swagger";
import type { ExibirMonitorResponse } from "../../model";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { SelectStyle } from "../../config/select";
import { getSaudeDescricao, getSaudeIcon } from "../../utils/saude";
import { gerarPDFTarefa } from "../../utils/pdf";

import AlertasCard from "../../components/AlertasCard";
import TarefasCard from "../../components/TarefasCard";
import TarefaCard from "../../components/TarefaCard";
import Layout from "../../components/Layout";
import { PageSize, SkeletonCount } from "../../config/defines";
import InfiniteScroll from "../../components/InfiniteScroll";

export default function MonitorFormulario() {
	const navigate = useNavigate();
	const { id, task } = useParams();
	const { handleToast } = useToast();
	const { handleLogout } = useAuth();
	const queryClient = useQueryClient();

	const [searchParams] = useSearchParams();
	const grupoId = searchParams.get("grupoId");

	const [formStatus, setFormStatus] = useState(id ? false : true);
	const [formSaving, setFormSaving] = useState(false);

	const [formGrupo, setFormGrupo] = useState<{ label: string; value: number }>();
	const [formGrupoOptions, setFormGruposOptions] = useState<{ label: string; value: number }[]>();
	const [formNome, setFormNome] = useState("");
	const [formDescricao, setFormDescricao] = useState("");
	const [formUrl, setFormUrl] = useState("");
	const [formMetodo, setFormMetodo] = useState("");
	const [formHeaders, setFormHeaders] = useState<[string, string][] | undefined>();
	const [formPayload, setFormPayload] = useState("");
	const [formPayloadType, setFormPayloadType] = useState<string>("");
	const [formPayloadFields, setFormPayloadFields] = useState<[string, string][]>([]);
	const [formIntervalo, setFormIntervalo] = useState(1);
	const [formTolerancia, setFormTolerancia] = useState(1);
	const [formTimeout, setFormTimeout] = useState(1);
	const [formCondicoes, setFormCondicoes] = useState("");
	const [formCondicoesTest, setFormCondicoesTest] = useState<{ isValid: boolean; error?: string } | null>(null);
	const [formVariaveis, setFormVariaveis] = useState<{ nome: string; contexto: string; expressao: string }[] | undefined>();
	const [formVariaveisOptions, setFormVariaveisOptions] = useState<{ label: string; value: string; tipo: string }[]>();
	const [formVariaveisSystemOptions, setFormVariaveisSystemOptions] = useState<{ label: string; value: string; tipo: string }[]>();
	const [formVariaveisTest, setFormVariaveisTest] = useState<{ [key: number]: { isValid: boolean; error?: string } | null }>({});
	const [formRemoveAssetsFromPayloadResponse, setFormRemoveAssetsFromPayloadResponse] = useState(false);
	const [formAtivo, setFormAtivo] = useState(true);

	const [curlCommand, setCurlCommand] = useState("");
	const [regexTest, setRegexTest] = useState("");
	const [regexTestResult, setRegexTestResult] = useState<{ isValid: boolean; matches: string[] | null; error?: string } | null>(null);
	const [variaveisTestHeader, setVariaveisTestHeader] = useState("");
	const [variaveisTestPayload, setVariaveisTestPayload] = useState("");
	const [variaveisTestResults, setVariaveisTestResults] = useState<{ [key: number]: { isValid: boolean; matches: string[] | null; error?: string } | null }>({});
	const [tarefa, setTarefa] = useState<NonNullable<ExibirMonitorResponse["tasks"]>[number]["id"]>();

	const [tarefaModal, setTarefaModal] = useState(false);
	const [curlModal, setCurlModal] = useState(false);
	const [tab, setTab] = useState("tarefas");
	const [tarefas, setTarefas] = useState(false);

	const queryGrupos = useGetGruposListar({ offset: 0 });
	const queryVariaveis = useGetVariaveisListar({ offset: 0, showSystem: true });
	const queryTarefa = useGetMonitoresTasksTask({ id: tarefa }, { query: { enabled: !!id && !!tarefa } });
	const queryTarefas = useInfiniteQuery(
		getGetMonitoresTasksListarQueryKey({ monitorId: Number(id), offset: 0, limit: PageSize }),
		({ pageParam = 0 }) => getMonitoresTasksListar({ monitorId: Number(id), offset: pageParam * PageSize, limit: PageSize }),
		{
			getNextPageParam: (lastPage, allPages) => (lastPage.Data?.length === PageSize ? allPages.length : null),
		},
	);

	const { data, isLoading, isFetching, isRefetching } = useGetMonitoresMonitor({ id: Number(id) }, { query: { enabled: !!id && !formStatus } });

	const mutationPost = usePostMonitoresCadastrar({ mutation: { onSuccess: mutateSuccess } });
	const mutationPut = usePostMonitoresAtualizar({ mutation: { onSuccess: mutateSuccess } });

	useEffect(() => {
		if (queryGrupos.data?.Data && data?.Data) {
			setFormGrupo({
				label: queryGrupos.data.Data.find((grupo) => grupo.id === data.Data?.grupoId)?.nome || "",
				value: data.Data.grupoId!,
			});

			if (task) {
				setTarefa(Number(task));
				setTarefaModal(true);
			}
		}
	}, [queryGrupos.data, data, task]);

	useEffect(() => {
		if (formCondicoes) {
			try {
				//eslint-disable-next-line
				const regex = new RegExp(formCondicoes);
				setFormCondicoesTest({ isValid: true });
			} catch (error: any) {
				setFormCondicoesTest({ isValid: false, error: error.message || "Expressão regular inválida" });
			}
		}
	}, [formCondicoes]);

	useEffect(() => {
		if (queryGrupos.data?.Result === 1 && queryGrupos.data?.Data) {
			setFormGruposOptions(
				queryGrupos.data.Data.map((item) => {
					return { label: `${item.nome}`, value: item.id! };
				}),
			);
			if (queryGrupos.data.Data.length === 1) {
				setFormGrupo({ label: `${queryGrupos.data.Data[0].nome}`, value: queryGrupos.data.Data[0].id! });
			}
			if (grupoId) {
				setFormGrupo({
					label: queryGrupos.data.Data.find((grupo) => grupo.id === Number(grupoId))?.nome || "",
					value: Number(grupoId),
				});
			}
		} else if (queryGrupos.data?.Result === 99) {
			handleLogout();
			navigate("/");
		} else if (queryGrupos.data && queryGrupos.data.Result !== 1) {
			handleToast("Monitor", queryGrupos.data.Message || "Erro ao carregar grupos", 5000, "warning");
			handleVoltar();
		}
	}, [queryGrupos.data, grupoId]);

	useEffect(() => {
		if (queryVariaveis.data?.Result === 1 && queryVariaveis.data?.Data) {
			setFormVariaveisOptions(
				queryVariaveis.data.Data.filter((item) => item.tipo !== "system").map((item) => {
					return { label: item.nome!, value: item.nome!, tipo: item.tipo! };
				}),
			);
			setFormVariaveisSystemOptions(
				queryVariaveis.data.Data.map((item) => {
					return { label: item.nome!, value: item.nome!, tipo: item.tipo! };
				}),
			);
		} else if (queryVariaveis.data?.Result === 99) {
			handleLogout();
			navigate("/");
		} else if (queryVariaveis.data && queryVariaveis.data.Result !== 1) {
			handleToast("Monitor", queryVariaveis.data.Message || "Erro ao carregar variáveis", 5000, "warning");
			handleVoltar();
		}
	}, [queryVariaveis.data]);

	useEffect(() => {
		if (queryTarefa.data?.Result === 99) {
			handleLogout();
			navigate("/");
		}
	}, [queryTarefa.data]);

	useEffect(() => {
		if (data?.Result === 1 && data?.Data) {
			setFormNome(data.Data.nome || "");
			setFormDescricao(data.Data.descricao || "");
			setFormUrl(data.Data.url || "");
			setFormMetodo(data.Data.metodo || "");
			const loadedHeaders = data.Data.headers && data.Data.headers.length > 0 ? data.Data.headers.map((header: any) => [header[0] || "", header[1] || ""] as [string, string]) : undefined;
			setFormHeaders(loadedHeaders);
			setFormPayload(data.Data.payload || "");
			// Detectar tipo de payload a partir do header Content-Type
			const contentTypeHeader = loadedHeaders?.find((h) => h[0].toLowerCase() === "content-type");
			if (contentTypeHeader) {
				const detectedType = detectPayloadType(contentTypeHeader[1]);
				setFormPayloadType(detectedType);
				// Se for tipo form, converter payload para campos
				if (detectedType === "multipart/form-data" || detectedType === "application/x-www-form-urlencoded") {
					setFormPayloadFields(parsePayloadToFields(data.Data.payload || ""));
				}
			}
			setFormIntervalo(data.Data.intervalo || 1);
			setFormTolerancia(data.Data.tolerancia || 1);
			setFormTimeout(data.Data.timeout || 1);
			setFormCondicoes(data.Data.condicoes || "");
			setFormVariaveis(data.Data.variaveis as any);
			setFormRemoveAssetsFromPayloadResponse(data.Data.removeAssetsFromPayloadResponse || false);
			setFormAtivo(data.Data.ativo === undefined ? true : data.Data.ativo);
		} else if (data?.Result === 99) {
			handleLogout();
			navigate("/");
		} else if (data && data.Result !== 1) {
			handleToast("Monitor", data.Message || "Erro ao carregar monitor", 5000, "warning");
			handleVoltar();
		}
	}, [data]);

	function mutateSuccess(resp: any) {
		if (resp.Result === 1 && resp.Data) {
			queryClient.invalidateQueries(getGetMonitoresMonitorQueryKey({ id: resp.Data.id }));
			handleToast("Monitor", "Informações salvas com sucesso!", 5000);
			navigate(`/monitorFormulario/${resp.Data.id}`);
		} else {
			if (resp.Result === 99) {
				handleLogout();
				navigate("/");
			}
			handleToast("Monitor", resp.Message, 5000, "danger");
		}
		handleCancel();
	}

	function handleVoltar() {
		navigate(`/monitores`);
	}

	function handleCancel() {
		setFormStatus(false);
		setFormSaving(false);
	}

	function handleAddHeader() {
		setFormHeaders(formHeaders ? [...formHeaders, ["", ""]] : [["", ""]]);
	}

	function handleRemoveHeader(index: number) {
		if (!formHeaders) return;
		const newHeaders = formHeaders.filter((_, i) => i !== index);
		setFormHeaders(newHeaders.length === 0 ? undefined : newHeaders);
	}

	function handleUpdateHeader(index: number, key: string, value: string) {
		if (!formHeaders) return;
		const newHeaders = [...formHeaders];
		newHeaders[index] = [key, value];
		setFormHeaders(newHeaders);
	}

	function handleAddVariavel() {
		setFormVariaveis(formVariaveis ? [...formVariaveis, { nome: "", contexto: "", expressao: "" }] : [{ nome: "", contexto: "", expressao: "" }]);
	}

	function handleRemoveVariavel(index: number) {
		if (!formVariaveis) return;
		const newVariaveis = formVariaveis.filter((_, i) => i !== index);
		setFormVariaveis(newVariaveis.length === 0 ? undefined : newVariaveis);
	}

	function handleUpdateVariavel(index: number, field: "nome" | "contexto" | "expressao", value: string) {
		if (!formVariaveis) return;
		const newVariaveis = [...formVariaveis];
		newVariaveis[index] = { ...newVariaveis[index], [field]: value };
		setFormVariaveis(newVariaveis);

		// Validar expressão regular quando o campo for "expressao"
		if (field === "expressao") {
			if (value.trim() === "") {
				setFormVariaveisTest((prev) => ({ ...prev, [index]: null }));
			} else {
				try {
					new RegExp(value);
					setFormVariaveisTest((prev) => ({ ...prev, [index]: { isValid: true } }));
				} catch (error: any) {
					setFormVariaveisTest((prev) => ({ ...prev, [index]: { isValid: false, error: error.message || "Expressão regular inválida" } }));
				}
			}
		}
	}

	const payloadTypeOptions = [
		{ label: "Texto", value: "text/plain" },
		{ label: "JSON", value: "application/json" },
		{ label: "Form Data", value: "multipart/form-data" },
		{ label: "Form URL Encoded", value: "application/x-www-form-urlencoded" },
	];

	function detectPayloadType(contentType: string): string {
		const normalizedType = contentType.toLowerCase().split(";")[0].trim();
		const matchedOption = payloadTypeOptions.find((opt) => normalizedType === opt.value);
		return matchedOption ? matchedOption.value : "text/plain";
	}

	function parsePayloadToFields(payload: string): [string, string][] {
		if (!payload.trim()) return [];
		return payload
			.split("\n")
			.filter((line) => line.trim())
			.map((line) => {
				const [key, ...valueParts] = line.split("=");
				return [key || "", valueParts.join("=") || ""] as [string, string];
			});
	}

	function fieldsToPayload(fields: [string, string][]): string {
		return fields
			.filter(([key, value]) => key.trim() !== "" || value.trim() !== "")
			.map(([key, value]) => `${key}=${value}`)
			.join("\n");
	}

	function updateContentTypeHeader(newType: string) {
		const newHeaders = formHeaders ? [...formHeaders] : [];
		const contentTypeIndex = newHeaders.findIndex((h) => h[0].toLowerCase() === "content-type");

		if (contentTypeIndex >= 0) {
			newHeaders[contentTypeIndex] = ["Content-Type", newType];
		} else {
			newHeaders.push(["Content-Type", newType]);
		}

		setFormHeaders(newHeaders);
	}

	function handlePayloadTypeChange(newType: string) {
		const oldType = formPayloadType;
		setFormPayloadType(newType);
		updateContentTypeHeader(newType);

		// Se mudando de texto para form, converter payload para campos
		if ((newType === "multipart/form-data" || newType === "application/x-www-form-urlencoded") && oldType !== "multipart/form-data" && oldType !== "application/x-www-form-urlencoded") {
			setFormPayloadFields(parsePayloadToFields(formPayload));
		}
		// Se mudando de form para texto, converter campos para payload
		else if ((oldType === "multipart/form-data" || oldType === "application/x-www-form-urlencoded") && newType !== "multipart/form-data" && newType !== "application/x-www-form-urlencoded") {
			setFormPayload(fieldsToPayload(formPayloadFields));
		}
	}

	function handleHeaderContentTypeChange(newValue: string, index: number) {
		handleUpdateHeader(index, formHeaders![index][0], newValue);
		const detectedType = detectPayloadType(newValue);
		const oldType = formPayloadType;
		setFormPayloadType(detectedType);

		// Se mudando de texto para form, converter payload para campos
		if ((detectedType === "multipart/form-data" || detectedType === "application/x-www-form-urlencoded") && oldType !== "multipart/form-data" && oldType !== "application/x-www-form-urlencoded") {
			setFormPayloadFields(parsePayloadToFields(formPayload));
		}
		// Se mudando de form para texto, converter campos para payload
		else if (
			(oldType === "multipart/form-data" || oldType === "application/x-www-form-urlencoded") &&
			detectedType !== "multipart/form-data" &&
			detectedType !== "application/x-www-form-urlencoded"
		) {
			setFormPayload(fieldsToPayload(formPayloadFields));
		}
	}

	function handleAddPayloadField() {
		const newFields = [...formPayloadFields, ["", ""] as [string, string]];
		setFormPayloadFields(newFields);
		setFormPayload(fieldsToPayload(newFields));
	}

	function handleRemovePayloadField(index: number) {
		const newFields = formPayloadFields.filter((_, i) => i !== index);
		setFormPayloadFields(newFields);
		setFormPayload(fieldsToPayload(newFields));
	}

	function handleUpdatePayloadField(index: number, key: string, value: string) {
		const newFields = [...formPayloadFields];
		newFields[index] = [key, value];
		setFormPayloadFields(newFields);
		setFormPayload(fieldsToPayload(newFields));
	}

	const isFormPayloadType = formPayloadType === "multipart/form-data" || formPayloadType === "application/x-www-form-urlencoded";

	function parseCurlCommand(curlCommand: string) {
		try {
			// Remove quebras de linha com barra invertida e normaliza espaços
			const cleanCommand = curlCommand
				.replace(/\\\s*[\r\n]+/g, " ")
				.replace(/\s+/g, " ")
				.trim();

			// Inicializar objeto de retorno
			const result: {
				method?: string;
				url?: string;
				headers?: [string, string][];
				payload?: string;
				payloadType?: string;
				payloadFields?: [string, string][];
			} = {};

			const headers: [string, string][] = [];

			// Extrair método HTTP (-X ou --request)
			const methodMatch = cleanCommand.match(/(?:-X|--request)\s+['"]?([A-Z]+)['"]?/i);
			if (methodMatch) {
				result.method = methodMatch[1].toUpperCase();
			}

			// Verificar se é HEAD request (-I ou --head)
			if (/(?:^|\s)(?:-I|--head)(?:\s|$)/.test(cleanCommand)) {
				result.method = "HEAD";
			}

			// Extrair URL - múltiplas estratégias
			let url: string | undefined;

			// Estratégia 1: URL com aspas simples
			const urlSingleQuoteMatch = cleanCommand.match(/['](https?:\/\/[^']+)['"]/);
			if (urlSingleQuoteMatch) {
				url = urlSingleQuoteMatch[1];
			}

			// Estratégia 2: URL com aspas duplas
			if (!url) {
				const urlDoubleQuoteMatch = cleanCommand.match(/["](https?:\/\/[^"]+)["]/);
				if (urlDoubleQuoteMatch) {
					url = urlDoubleQuoteMatch[1];
				}
			}

			// Estratégia 3: --url flag
			if (!url) {
				const urlFlagMatch = cleanCommand.match(/--url\s+['"]?([^\s'"]+)['"]?/);
				if (urlFlagMatch) {
					url = urlFlagMatch[1];
				}
			}

			// Estratégia 4: URL sem aspas (procura por http:// ou https://)
			if (!url) {
				const urlHttpMatch = cleanCommand.match(/(?:^|\s)(https?:\/\/[^\s'"]+)/);
				if (urlHttpMatch) {
					url = urlHttpMatch[1];
				}
			}

			if (url) {
				result.url = url;
			}

			// Extrair autenticação básica (-u ou --user)
			const userMatch = cleanCommand.match(/(?:-u|--user)\s+['"]?([^'"\s]+)['"]?/);
			if (userMatch) {
				const credentials = userMatch[1];
				const base64Credentials = btoa(credentials);
				headers.push(["Authorization", `Basic ${base64Credentials}`]);
			}

			// Extrair User-Agent (-A ou --user-agent)
			const userAgentMatch = cleanCommand.match(/(?:-A|--user-agent)\s+['"]([^'"]+)['"]/);
			if (userAgentMatch) {
				headers.push(["User-Agent", userAgentMatch[1]]);
			}

			// Extrair cookies (-b ou --cookie)
			const cookieMatch = cleanCommand.match(/(?:-b|--cookie)\s+['"]([^'"]+)['"]/);
			if (cookieMatch) {
				headers.push(["Cookie", cookieMatch[1]]);
			}

			// Extrair Referer (-e ou --referer)
			const refererMatch = cleanCommand.match(/(?:-e|--referer)\s+['"]?([^\s'"]+)['"]?/);
			if (refererMatch) {
				headers.push(["Referer", refererMatch[1]]);
			}

			// Extrair headers explícitos (-H ou --header)
			const headerMatches = Array.from(cleanCommand.matchAll(/(?:-H|--header)\s+['"]([^'"]+)['"]/g));
			for (const match of headerMatches) {
				const headerString = match[1];
				const colonIndex = headerString.indexOf(":");
				if (colonIndex > 0) {
					const key = headerString.substring(0, colonIndex).trim();
					const value = headerString.substring(colonIndex + 1).trim();
					headers.push([key, value]);
				}
			}

			// Verificar --compressed (adiciona Accept-Encoding se não existir)
			if (/(?:^|\s)--compressed(?:\s|$)/.test(cleanCommand)) {
				const hasAcceptEncoding = headers.some(([key]) => key.toLowerCase() === "accept-encoding");
				if (!hasAcceptEncoding) {
					headers.push(["Accept-Encoding", "gzip, deflate, br"]);
				}
			}

			// Extrair dados do corpo da requisição
			let payload: string | undefined;

			// --data-urlencode (pode ter múltiplos)
			const dataUrlEncodeMatches = Array.from(cleanCommand.matchAll(/--data-urlencode\s+['"]?([^'"\s]+(?:=[^'"\s]*)?)['"]?/g));
			if (dataUrlEncodeMatches.length > 0) {
				const encodedParts: string[] = [];
				for (const match of dataUrlEncodeMatches) {
					const part = match[1];
					if (part.includes("=")) {
						const [key, ...valueParts] = part.split("=");
						const value = valueParts.join("=");
						encodedParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
					} else {
						encodedParts.push(encodeURIComponent(part));
					}
				}
				payload = encodedParts.join("&");
			}

			// --form ou -F (multipart/form-data)
			const formMatches = Array.from(cleanCommand.matchAll(/(?:-F|--form)\s+['"]([^'"]+)['"]/g));
			if (formMatches.length > 0) {
				const formParts: string[] = [];
				for (const match of formMatches) {
					formParts.push(match[1]);
				}
				payload = formParts.join("\n");
				// Adiciona Content-Type multipart se não existir
				const hasContentType = headers.some(([key]) => key.toLowerCase() === "content-type");
				if (!hasContentType) {
					headers.push(["Content-Type", "multipart/form-data"]);
				}
			}

			// -d, --data, --data-raw, --data-binary, --data-ascii (pode ter múltiplos)
			if (!payload) {
				// Primeiro tenta capturar com aspas simples (mais comum com JSON)
				let dataMatches = Array.from(cleanCommand.matchAll(/(?:-d|--data|--data-raw|--data-binary|--data-ascii)\s+'((?:[^'\\]|\\.)*)'/g));

				// Se não encontrou, tenta com aspas duplas
				if (dataMatches.length === 0) {
					dataMatches = Array.from(cleanCommand.matchAll(/(?:-d|--data|--data-raw|--data-binary|--data-ascii)\s+"((?:[^"\\]|\\.)*)"/g));
				}

				// Fallback: tenta sem aspas (dados simples)
				if (dataMatches.length === 0) {
					dataMatches = Array.from(cleanCommand.matchAll(/(?:-d|--data|--data-raw|--data-binary|--data-ascii)\s+([^\s'"]+)/g));
				}

				if (dataMatches.length > 0) {
					const dataParts: string[] = [];
					for (const match of dataMatches) {
						dataParts.push(match[1]);
					}
					payload = dataParts.join("&");
				}
			}

			// --json (curl 7.82+) - automaticamente define Content-Type e Accept
			let jsonMatch = cleanCommand.match(/--json\s+'((?:[^'\\]|\\.)*)'/);
			if (!jsonMatch) {
				jsonMatch = cleanCommand.match(/--json\s+"((?:[^"\\]|\\.)*)"/);
			}
			if (jsonMatch) {
				payload = jsonMatch[1];
				const hasContentType = headers.some(([key]) => key.toLowerCase() === "content-type");
				if (!hasContentType) {
					headers.push(["Content-Type", "application/json"]);
				}
				const hasAccept = headers.some(([key]) => key.toLowerCase() === "accept");
				if (!hasAccept) {
					headers.push(["Accept", "application/json"]);
				}
			}

			// Formatar payload se for JSON válido
			if (payload) {
				try {
					const jsonObj = JSON.parse(payload);
					payload = JSON.stringify(jsonObj, null, 2);
				} catch {
					// Se não for JSON válido, mantém como está
				}
				result.payload = payload;
			}

			// Inferir método HTTP se não foi especificado
			if (!result.method) {
				// Se tem dados, assume POST
				const hasData = /(?:-d|--data|--data-raw|--data-binary|--data-ascii|--data-urlencode|--json|-F|--form)/.test(cleanCommand);
				// Se tem -G ou --get, força GET mesmo com dados
				const forceGet = /(?:^|\s)(?:-G|--get)(?:\s|$)/.test(cleanCommand);
				result.method = hasData && !forceGet ? "POST" : "GET";
			}

			if (headers.length > 0) {
				result.headers = headers;
			}

			// Detectar payloadType baseado no Content-Type header
			const contentTypeHeader = headers.find(([key]) => key.toLowerCase() === "content-type");
			if (contentTypeHeader) {
				const contentTypeValue = contentTypeHeader[1].toLowerCase().split(";")[0].trim();

				// Mapear Content-Type para payloadType
				if (contentTypeValue.includes("application/json")) {
					result.payloadType = "application/json";
				} else if (contentTypeValue.includes("application/xml") || contentTypeValue.includes("text/xml")) {
					result.payloadType = "application/xml";
				} else if (contentTypeValue.includes("text/html")) {
					result.payloadType = "text/html";
				} else if (contentTypeValue.includes("multipart/form-data")) {
					result.payloadType = "multipart/form-data";
				} else if (contentTypeValue.includes("application/x-www-form-urlencoded")) {
					result.payloadType = "application/x-www-form-urlencoded";
				} else if (contentTypeValue.includes("text/plain")) {
					result.payloadType = "text/plain";
				} else {
					result.payloadType = "text/plain";
				}
			} else {
				// Se não tem Content-Type, inferir pelo payload
				if (result.payload) {
					try {
						JSON.parse(result.payload);
						result.payloadType = "application/json";
					} catch {
						if (result.payload.includes("<") && result.payload.includes(">")) {
							result.payloadType = result.payload.toLowerCase().includes("<!doctype") || result.payload.toLowerCase().includes("<html") ? "text/html" : "application/xml";
						} else {
							result.payloadType = "text/plain";
						}
					}
				}
			}

			// Se for form-data ou url-encoded, converter payload para campos
			if (result.payloadType === "multipart/form-data" || result.payloadType === "application/x-www-form-urlencoded") {
				if (result.payload) {
					// Tentar parsear como query string (key=value&key2=value2)
					const fields: [string, string][] = [];
					const pairs = result.payload.split(/[&\n]/);
					for (const pair of pairs) {
						if (pair.trim()) {
							const [key, ...valueParts] = pair.split("=");
							const value = valueParts.join("=");
							try {
								// Tentar decodificar URL encoding
								fields.push([decodeURIComponent(key || ""), decodeURIComponent(value || "")]);
							} catch {
								fields.push([key || "", value || ""]);
							}
						}
					}
					result.payloadFields = fields;
					// Converter payload para formato de campos (key=value por linha)
					result.payload = fields.map(([k, v]) => `${k}=${v}`).join("\n");
				}
			}

			return result;
		} catch (error) {
			console.error("Erro ao fazer parse do comando cURL:", error);
			return null;
		}
	}

	function generateCurlCommand(): string {
		let curl = "curl";

		// Adicionar método
		if (formMetodo && formMetodo.toUpperCase() !== "GET") {
			curl += ` -X ${formMetodo.toUpperCase()}`;
		}

		// Adicionar URL
		if (formUrl) {
			curl += ` '${formUrl}'`;
		}

		// Adicionar headers
		if (formHeaders && formHeaders.length > 0) {
			for (const [key, value] of formHeaders) {
				if (key.trim() !== "" || value.trim() !== "") {
					curl += ` \\\n  -H '${key}: ${value}'`;
				}
			}
		}

		// Adicionar payload
		if (formPayload && formPayload.trim() !== "") {
			// Verificar se é tipo form (form-data ou url-encoded)
			if (isFormPayloadType && formPayloadFields && formPayloadFields.length > 0) {
				// Para form types, cada campo deve ser um parâmetro -d separado
				for (const [key, value] of formPayloadFields) {
					if (key.trim() !== "" || value.trim() !== "") {
						// Escapar aspas simples
						const escapedKey = key.replace(/'/g, "'\\''");
						const escapedValue = value.replace(/'/g, "'\\''");
						curl += ` \\\n  -d '${escapedKey}=${escapedValue}'`;
					}
				}
			} else {
				// Para outros tipos (JSON, texto), usar payload direto
				const escapedPayload = formPayload.replace(/'/g, "'\\''");
				curl += ` \\\n  -d '${escapedPayload}'`;
			}
		}

		return curl;
	}

	async function handleExportCurl() {
		const curl = generateCurlCommand();
		setCurlCommand(curl);

		// Copiar para a área de transferência
		try {
			await navigator.clipboard.writeText(curl);
			handleToast("Monitor", "Comando copiado para a área de transferência!", 5000);
		} catch (error) {
			handleToast("Monitor", "Não foi possível copiar para a área de transferência", 5000, "warning");
		}
	}

	function testRegex() {
		if (!formCondicoes || !regexTest) {
			setRegexTestResult(null);
			return;
		}

		try {
			const regex = new RegExp(formCondicoes);
			const matches = regexTest.match(regex);

			setRegexTestResult({
				isValid: true,
				matches: matches ? Array.from(matches) : null,
			});
		} catch (error: any) {
			setRegexTestResult({
				isValid: false,
				matches: null,
				error: error.message || "Expressão regular inválida",
			});
		}
	}

	function testVariaveisRegex() {
		if (!formVariaveis || formVariaveis.length === 0) {
			setVariaveisTestResults({});
			return;
		}

		const results: { [key: number]: { isValid: boolean; matches: string[] | null; error?: string } | null } = {};

		formVariaveis.forEach((variavel, index) => {
			if (!variavel.expressao || variavel.expressao.trim() === "") {
				results[index] = null;
				return;
			}

			const testText = variavel.contexto === "Response.Header" ? variaveisTestHeader : variaveisTestPayload;

			if (!testText || testText.trim() === "") {
				results[index] = null;
				return;
			}

			try {
				const regex = new RegExp(variavel.expressao);
				const matches = testText.match(regex);

				results[index] = {
					isValid: true,
					matches: matches ? Array.from(matches) : null,
				};
			} catch (error: any) {
				results[index] = {
					isValid: false,
					matches: null,
					error: error.message || "Expressão regular inválida",
				};
			}
		});

		setVariaveisTestResults(results);
	}

	async function handlePDF() {
		if (!queryTarefa.data?.Data) return;

		const result = await gerarPDFTarefa({
			tarefa: queryTarefa.data.Data,
			monitorId: id!,
			monitorNome: formNome,
			monitorDescricao: formDescricao,
			grupoNome: formGrupo?.label,
		});

		if (result.success) {
			handleToast("Monitor", "PDF gerado com sucesso!", 5000);
		} else {
			handleToast("Monitor", result.error || "Erro ao gerar PDF", 5000, "danger");
		}
	}

	const iconClass = getSaudeIcon(data?.Data?.saude);
	const saudeDescricao = getSaudeDescricao(data?.Data?.saude);
	const contextoOptions = [
		{ label: "Response.Header", value: "Response.Header" },
		{ label: "Response.Payload", value: "Response.Payload" },
	];

	return (
		<Layout>
			<Form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setFormSaving(true);
					if (id) {
						mutationPut.mutate({
							data: {
								id: Number(id),
								grupoId: formGrupo ? formGrupo.value : 0,
								nome: formNome,
								descricao: formDescricao,
								url: formUrl,
								metodo: formMetodo,
								headers: formHeaders ? formHeaders.filter((header) => header[0].trim() !== "" || header[1].trim() !== "") : undefined,
								payload: formPayload,
								intervalo: formIntervalo,
								tolerancia: formTolerancia,
								timeout: formTimeout,
								condicoes: formCondicoes,
								variaveis: formVariaveis ? formVariaveis.filter((v) => v.nome.trim() !== "" || v.contexto.trim() !== "" || v.expressao.trim() !== "") : undefined,
								removeAssetsFromPayloadResponse: formRemoveAssetsFromPayloadResponse,
								ativo: formAtivo,
							},
						});
					} else {
						mutationPost.mutate({
							data: {
								grupoId: formGrupo ? formGrupo.value : 0,
								nome: formNome,
								descricao: formDescricao,
								url: formUrl,
								metodo: formMetodo,
								headers: formHeaders ? formHeaders.filter((header) => header[0].trim() !== "" || header[1].trim() !== "") : undefined,
								payload: formPayload,
								intervalo: formIntervalo,
								tolerancia: formTolerancia,
								timeout: formTimeout,
								condicoes: formCondicoes,
								variaveis: formVariaveis ? formVariaveis.filter((v) => v.nome.trim() !== "" || v.contexto.trim() !== "") : undefined,
								removeAssetsFromPayloadResponse: formRemoveAssetsFromPayloadResponse,
							},
						});
					}
				}}
			>
				<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
					<Link to={"/monitores"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiPulse} size={1} className="me-2" /> Formulário de Monitor
					</Link>
					{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex gap-2 float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
						<Button
							variant="outline-dark"
							type="button"
							onClick={() => {
								setCurlModal(true);
							}}
							disabled={formSaving}
						>
							cURL
						</Button>
						{!formStatus && (
							<Button
								variant="dark"
								onClick={() => {
									setFormStatus(true);
								}}
							>
								Editar Informações
							</Button>
						)}
						{formStatus && (
							<>
								<Button
									variant="light"
									type="button"
									className="rounded-3 shadow-sm"
									onClick={() => {
										if (id) {
											setFormStatus(false);
										} else {
											navigate("/monitores");
										}
									}}
									disabled={formSaving}
								>
									Cancelar
								</Button>
								<Button variant="primary" type="submit" disabled={formSaving}>
									{formSaving ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" /> Salvando
										</>
									) : (
										"Salvar Informações"
									)}
								</Button>
							</>
						)}
					</div>
				</h5>

				<Card className="mb-3">
					<Card.Header className="d-flex align-items-center bg-white fs-5 fw-light p-3">Informações</Card.Header>
					<Card.Body>
						<Form.Group className="mb-3" controlId="grupo">
							<Form.Label>Grupo</Form.Label>
							<Select
								placeholder={"Selecione o grupo"}
								defaultValue={formGrupo}
								value={formGrupo}
								noOptionsMessage={() => {
									return "Nenhuma opção disponível";
								}}
								options={formGrupoOptions}
								isLoading={queryGrupos.isLoading}
								onChange={(value) => {
									if (value) {
										setFormGrupo(value);
									}
								}}
								isDisabled={!formStatus || !!id}
								styles={SelectStyle}
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="nome">
							<Form.Label>Nome</Form.Label>
							<Form.Control
								type="text"
								placeholder="Informe aqui o nome"
								value={formNome}
								onChange={(event) => {
									setFormNome(event.target.value);
								}}
								disabled={!formStatus}
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="descricao">
							<Form.Label>Descrição</Form.Label>
							<Form.Control
								type="text"
								placeholder="Informe aqui a descrição"
								value={formDescricao}
								onChange={(event) => {
									setFormDescricao(event.target.value);
								}}
								disabled={!formStatus}
							/>
						</Form.Group>
						<Row>
							<Form.Group className="mb-3 col-md-6" controlId="metodo">
								<Form.Label>Método</Form.Label>
								<Form.Control
									type="text"
									placeholder="Informe aqui o método"
									value={formMetodo}
									onChange={(event) => {
										setFormMetodo(event.target.value);
									}}
									disabled={!formStatus}
								/>
							</Form.Group>
							<Form.Group className="mb-3 col-md-6" controlId="url">
								<Form.Label>URL</Form.Label>
								<Form.Control
									type="link"
									placeholder="Informe aqui a URL"
									value={formUrl}
									onChange={(event) => {
										setFormUrl(event.target.value);
									}}
									disabled={!formStatus}
								/>
							</Form.Group>
						</Row>
						<Form.Group className="mb-3" controlId="headers">
							<Form.Label className="d-flex align-items-center justify-content-between">
								Headers
								{formStatus && (
									<Button variant="secondary" size="sm" onClick={handleAddHeader} className="d-flex align-items-center">
										<Icon path={mdiPlus} size={0.7} className="me-1" />
										Adicionar
									</Button>
								)}
							</Form.Label>
							{formHeaders ? (
								formHeaders.map((header, index) => {
									const isContentType = header[0].toLowerCase() === "content-type";
									return (
										<Row key={index} className="mb-2">
											<Col>
												<Form.Control
													type="text"
													placeholder="Informe aqui o nome"
													value={header[0]}
													onChange={(event) => {
														handleUpdateHeader(index, event.target.value, header[1]);
													}}
													disabled={!formStatus}
												/>
											</Col>
											<Col>
												<InputGroup>
													<Form.Control
														type="text"
														placeholder="Informe aqui o valor"
														value={header[1]}
														onChange={(event) => {
															if (isContentType) {
																handleHeaderContentTypeChange(event.target.value, index);
															} else {
																handleUpdateHeader(index, header[0], event.target.value);
															}
														}}
														disabled={!formStatus}
													/>
													<Dropdown>
														<Dropdown.Toggle variant="outline-secondary" size="sm" disabled={!formStatus || queryVariaveis.isLoading}>
															Variáveis
														</Dropdown.Toggle>
														<Dropdown.Menu className="shadow p-0">
															{formVariaveisSystemOptions && formVariaveisSystemOptions.length > 0 ? (
																formVariaveisSystemOptions.map((variavel, varIndex) => (
																	<Dropdown.Item
																		key={varIndex}
																		onClick={() => {
																			const newValue = header[1] + `%${variavel.value}%`;
																			handleUpdateHeader(index, header[0], newValue);
																		}}
																	>
																		{variavel.label}{" "}
																		{variavel.tipo === "system" && (
																			<OverlayTrigger
																				overlay={
																					<Tooltip id={`tooltip-sistema-${varIndex}`}>
																						<div className="text-start" style={{ textWrap: "nowrap" }}>
																							<code>%DATE%</code> — Data atual. Ex: <code>%DATE+86400:YYYY-MM-DD%</code>
																						</div>
																						<div className="text-start" style={{ textWrap: "nowrap" }}>
																							<code>%TIMESTAMP%</code> — Unix timestamp. Ex: <code>%TIMESTAMP+3600%</code>
																						</div>
																						<div className="text-start" style={{ textWrap: "nowrap" }}>
																							<code>%UUID%</code> — ID único por execução
																						</div>
																					</Tooltip>
																				}
																			>
																				<Badge bg="secondary">SISTEMA</Badge>
																			</OverlayTrigger>
																		)}
																	</Dropdown.Item>
																))
															) : (
																<Dropdown.Item disabled>Nenhuma variável disponível</Dropdown.Item>
															)}
														</Dropdown.Menu>
													</Dropdown>
												</InputGroup>
											</Col>
											<Col md="auto">
												<Button
													variant="light"
													onClick={() => {
														if (isContentType) {
															setFormPayloadType("");
														}
														handleRemoveHeader(index);
													}}
													disabled={!formStatus}
												>
													<Icon path={mdiDeleteOutline} className="text-danger" size={1} />
												</Button>
											</Col>
										</Row>
									);
								})
							) : (
								<div className="text-muted">Nenhum header configurado</div>
							)}
						</Form.Group>
						<Form.Group className="mb-3" controlId="payload">
							<div className="d-flex align-items-center justify-content-between">
								<Form.Label>Payload</Form.Label>
								<div className="d-flex align-items-center gap-2 mb-1">
									{formStatus && isFormPayloadType && (
										<Button variant="secondary" size="sm" onClick={handleAddPayloadField} className="d-flex align-items-center">
											<Icon path={mdiPlus} size={0.7} className="me-1" />
											Adicionar
										</Button>
									)}
									<Dropdown>
										<Dropdown.Toggle variant="outline-secondary" size="sm" disabled={!formStatus}>
											{payloadTypeOptions.find((opt) => opt.value === formPayloadType)?.label || "Tipo de conteúdo"}
										</Dropdown.Toggle>
										<Dropdown.Menu className="shadow p-0">
											{payloadTypeOptions.map((option, varIndex) => (
												<Dropdown.Item
													key={varIndex}
													onClick={() => {
														handlePayloadTypeChange(option.value);
													}}
												>
													{option.label}
												</Dropdown.Item>
											))}
										</Dropdown.Menu>
									</Dropdown>
								</div>
							</div>
							{isFormPayloadType ? (
								<>
									{formPayloadFields.length > 0 ? (
										formPayloadFields.map((field, index) => (
											<Row key={index} className="mb-2">
												<Col>
													<Form.Control
														type="text"
														placeholder="Chave"
														value={field[0]}
														onChange={(event) => {
															handleUpdatePayloadField(index, event.target.value, field[1]);
														}}
														disabled={!formStatus}
													/>
												</Col>
												<Col>
													<InputGroup>
														<Form.Control
															type="text"
															placeholder="Valor"
															value={field[1]}
															onChange={(event) => {
																handleUpdatePayloadField(index, field[0], event.target.value);
															}}
															disabled={!formStatus}
														/>
														<Dropdown>
															<Dropdown.Toggle variant="outline-secondary" size="sm" disabled={!formStatus || queryVariaveis.isLoading}>
																Variáveis
															</Dropdown.Toggle>
															<Dropdown.Menu className="shadow p-0">
																{formVariaveisSystemOptions && formVariaveisSystemOptions.length > 0 ? (
																	formVariaveisSystemOptions.map((variavel, varIndex) => (
																		<Dropdown.Item
																			key={varIndex}
																			onClick={() => {
																				handleUpdatePayloadField(index, field[0], field[1] + `%${variavel.value}%`);
																			}}
																		>
																			{variavel.label}{" "}
																			{variavel.tipo === "system" && (
																				<OverlayTrigger
																					overlay={
																						<Tooltip id={`tooltip-sistema-${varIndex}`}>
																							<div className="text-start" style={{ textWrap: "nowrap" }}>
																								<code>%DATE%</code> — Data atual. Ex: <code>%DATE+86400:YYYY-MM-DD%</code>
																							</div>
																							<div className="text-start" style={{ textWrap: "nowrap" }}>
																								<code>%TIMESTAMP%</code> — Unix timestamp. Ex: <code>%TIMESTAMP+3600%</code>
																							</div>
																							<div className="text-start" style={{ textWrap: "nowrap" }}>
																								<code>%UUID%</code> — ID único por execução
																							</div>
																						</Tooltip>
																					}
																				>
																					<Badge bg="secondary">SISTEMA</Badge>
																				</OverlayTrigger>
																			)}
																		</Dropdown.Item>
																	))
																) : (
																	<Dropdown.Item disabled>Nenhuma variável disponível</Dropdown.Item>
																)}
															</Dropdown.Menu>
														</Dropdown>
													</InputGroup>
												</Col>
												<Col md="auto">
													<Button variant="light" onClick={() => handleRemovePayloadField(index)} disabled={!formStatus}>
														<Icon path={mdiDeleteOutline} className="text-danger" size={1} />
													</Button>
												</Col>
											</Row>
										))
									) : (
										<div className="text-muted">Nenhum campo configurado</div>
									)}
								</>
							) : (
								<InputGroup>
									<Form.Control
										type="text"
										as={"textarea"}
										rows={3}
										placeholder="Informe aqui o payload"
										value={formPayload}
										onChange={(event) => {
											setFormPayload(event.target.value);
										}}
										disabled={!formStatus}
									/>
									<Dropdown>
										<Dropdown.Toggle variant="outline-secondary" size="sm" disabled={!formStatus || queryVariaveis.isLoading}>
											Variáveis
										</Dropdown.Toggle>
										<Dropdown.Menu className="shadow p-0">
											{formVariaveisSystemOptions && formVariaveisSystemOptions.length > 0 ? (
												formVariaveisSystemOptions.map((variavel, varIndex) => (
													<Dropdown.Item
														key={varIndex}
														onClick={() => {
															setFormPayload(formPayload + `%${variavel.value}%`);
														}}
													>
														{variavel.label}{" "}
														{variavel.tipo === "system" && (
															<OverlayTrigger
																overlay={
																	<Tooltip id={`tooltip-sistema-${varIndex}`}>
																		<div className="text-start" style={{ textWrap: "nowrap" }}>
																			<code>%DATE%</code> — Data atual. Ex: <code>%DATE+86400:YYYY-MM-DD%</code>
																		</div>
																		<div className="text-start" style={{ textWrap: "nowrap" }}>
																			<code>%TIMESTAMP%</code> — Unix timestamp. Ex: <code>%TIMESTAMP+3600%</code>
																		</div>
																		<div className="text-start" style={{ textWrap: "nowrap" }}>
																			<code>%UUID%</code> — ID único por execução
																		</div>
																	</Tooltip>
																}
															>
																<Badge bg="secondary">SISTEMA</Badge>
															</OverlayTrigger>
														)}
													</Dropdown.Item>
												))
											) : (
												<Dropdown.Item disabled>Nenhuma variável disponível</Dropdown.Item>
											)}
										</Dropdown.Menu>
									</Dropdown>
								</InputGroup>
							)}
						</Form.Group>
						<Row>
							<Form.Group className="mb-3 col-md-4" controlId="intervalo">
								<Form.Label>Intervalo</Form.Label>
								<InputGroup>
									<Form.Control
										type="number"
										min={1}
										placeholder="Informe aqui o intervalo"
										value={formIntervalo}
										onChange={(event) => {
											setFormIntervalo(Number(event.target.value));
										}}
										disabled={!formStatus}
									/>
									<InputGroup.Text>minutos</InputGroup.Text>
								</InputGroup>
							</Form.Group>
							<Form.Group className="mb-3 col-md-4" controlId="tolerancia">
								<Form.Label>Tolerância</Form.Label>
								<Form.Control
									type="number"
									min={1}
									placeholder="Informe aqui a tolerância"
									value={formTolerancia}
									onChange={(event) => {
										setFormTolerancia(Number(event.target.value));
									}}
									disabled={!formStatus}
								/>
							</Form.Group>
							<Form.Group className="mb-3 col-md-4" controlId="timeout">
								<Form.Label>Timeout</Form.Label>
								<InputGroup>
									<Form.Control
										type="number"
										min={1}
										placeholder="Informe aqui o timeout"
										value={formTimeout}
										onChange={(event) => {
											setFormTimeout(Number(event.target.value));
										}}
										disabled={!formStatus}
									/>
									<InputGroup.Text>segundos</InputGroup.Text>
								</InputGroup>
							</Form.Group>
						</Row>
						<Form.Group className="mb-3" controlId="condicoes">
							<Form.Label>Condições</Form.Label>
							<InputGroup>
								<Form.Control
									type="text"
									placeholder="Digite a expressão regular para validar a resposta"
									value={formCondicoes}
									onChange={(event) => {
										setFormCondicoes(event.target.value);
										setRegexTestResult(null);
									}}
									disabled={!formStatus}
									isInvalid={formCondicoesTest?.isValid === false}
									isValid={formCondicoesTest?.isValid === true}
								/>
								<InputGroup.Text>expressão regular</InputGroup.Text>
								<Form.Control.Feedback type="invalid">{formCondicoesTest?.error}</Form.Control.Feedback>
							</InputGroup>
						</Form.Group>
						{formStatus && formCondicoes && (
							<Form.Group className="mb-3" controlId="regexTest">
								<Form.Label className="d-flex align-items-center justify-content-between">
									Condições Testar Expressão Regular
									<Button variant="secondary" size="sm" onClick={testRegex} disabled={!formCondicoesTest?.isValid}>
										Testar
									</Button>
								</Form.Label>
								<Form.Control
									type="text"
									placeholder="Digite um texto de exemplo para testar a regex"
									value={regexTest}
									onChange={(event) => {
										setRegexTest(event.target.value);
										setRegexTestResult(null);
									}}
									as="textarea"
									rows={2}
								/>
								{regexTestResult && (
									<Alert variant={regexTestResult.isValid ? (regexTestResult.matches ? "success" : "warning") : "danger"} className="mt-1 p-3">
										{regexTestResult.isValid ? (
											regexTestResult.matches ? (
												<>
													<div className="fw-bold">✓ Match encontrado!</div>
													<div className="mt-1">
														<ul className="mb-0 mt-1">
															{regexTestResult.matches.map((match, idx) => (
																<li key={idx}>
																	<code>{match}</code>
																</li>
															))}
														</ul>
													</div>
												</>
											) : (
												<div>⚠ Nenhum match encontrado</div>
											)
										) : (
											<div>✗ {regexTestResult.error}</div>
										)}
									</Alert>
								)}
							</Form.Group>
						)}

						<Form.Group className="mb-3" controlId="variaveis">
							<Form.Label className="d-flex align-items-center justify-content-between">
								Variáveis Atribuir Valor
								{formStatus && (
									<Button
										variant="secondary"
										size="sm"
										onClick={handleAddVariavel}
										className="d-flex align-items-center"
										disabled={formVariaveis && formVariaveisOptions && formVariaveis.length >= formVariaveisOptions.length}
									>
										<Icon path={mdiPlus} size={0.7} className="me-1" />
										Adicionar
									</Button>
								)}
							</Form.Label>
							{formVariaveis && formVariaveis.length > 0 ? (
								formVariaveis.map((variavel, index) => {
									const availableOptions = formVariaveisOptions?.filter((option) => !formVariaveis.some((v, i) => i !== index && v.nome === option.value));
									return (
										<Row key={index} className="mb-2">
											<Col md={3}>
												<Select
													placeholder="Selecione a variável"
													value={variavel.nome ? { label: variavel.nome, value: variavel.nome } : null}
													noOptionsMessage={() => "Nenhuma opção disponível"}
													options={availableOptions}
													isLoading={queryVariaveis.isLoading}
													onChange={(value) => {
														if (value) {
															handleUpdateVariavel(index, "nome", value.label);
														}
													}}
													isDisabled={!formStatus}
													styles={SelectStyle}
												/>
											</Col>
											<Col md={3}>
												<Select
													placeholder="Selecione o contexto"
													value={variavel.contexto ? { label: variavel.contexto, value: variavel.contexto } : null}
													noOptionsMessage={() => "Nenhuma opção disponível"}
													options={contextoOptions}
													onChange={(value) => {
														if (value) {
															handleUpdateVariavel(index, "contexto", value.value);
														}
													}}
													isDisabled={!formStatus}
													styles={SelectStyle}
												/>
											</Col>
											<Col>
												<Form.Control
													type="text"
													placeholder="Expressão"
													value={variavel.expressao}
													onChange={(event) => {
														handleUpdateVariavel(index, "expressao", event.target.value);
													}}
													disabled={!formStatus}
													isInvalid={formVariaveisTest[index]?.isValid === false}
													isValid={formVariaveisTest[index]?.isValid === true}
												/>
												<Form.Control.Feedback type="invalid">{formVariaveisTest[index]?.error}</Form.Control.Feedback>
											</Col>
											<Col md="auto">
												<Button variant="light" onClick={() => handleRemoveVariavel(index)} disabled={!formStatus}>
													<Icon path={mdiDeleteOutline} className="text-danger" size={1} />
												</Button>
											</Col>
										</Row>
									);
								})
							) : (
								<div className="text-muted">Nenhuma variável configurada</div>
							)}
						</Form.Group>

						{formStatus && formVariaveis && formVariaveis.length > 0 && (
							<>
								<Form.Group className="mb-3" controlId="variaveisTestHeader">
									<Form.Label className="d-flex align-items-center justify-content-between">
										Variáveis Testar Expressão Regular
										<Button variant="secondary" size="sm" onClick={testVariaveisRegex} disabled={!variaveisTestHeader && !variaveisTestPayload}>
											Testar
										</Button>
									</Form.Label>
									<InputGroup className="mb-1">
										<InputGroup.Text className="col-md-2">Response.Header</InputGroup.Text>
										<Form.Control
											type="text"
											placeholder="Digite um exemplo de Response.Header para testar as expressões das variáveis"
											value={variaveisTestHeader}
											onChange={(event) => {
												setVariaveisTestHeader(event.target.value);
												setVariaveisTestResults({});
											}}
											as="textarea"
											rows={2}
										/>
									</InputGroup>
									<InputGroup>
										<InputGroup.Text className="col-md-2">Response.Payload</InputGroup.Text>
										<Form.Control
											type="text"
											placeholder="Digite um exemplo de Response.Payload para testar as expressões das variáveis"
											value={variaveisTestPayload}
											onChange={(event) => {
												setVariaveisTestPayload(event.target.value);
												setVariaveisTestResults({});
											}}
											as="textarea"
											rows={2}
										/>
									</InputGroup>
								</Form.Group>
								{Object.keys(variaveisTestResults).length > 0 && (
									<div className="mb-3">
										<Form.Label>Resultados dos Testes</Form.Label>
										{formVariaveis.map((variavel, index) => {
											const result = variaveisTestResults[index];
											if (!result) return null;

											return (
												<Alert key={index} variant={result.isValid ? (result.matches ? "success" : "warning") : "danger"} className="mt-2 p-3">
													<div className="fw-bold">
														Variável: {variavel.nome || `#${index + 1}`} ({variavel.contexto})
													</div>
													<div className="text-muted small">Expressão: {variavel.expressao}</div>
													{result.isValid ? (
														result.matches ? (
															<>
																<div className="mt-1">✓ Match encontrado!</div>
																<div className="mt-1">
																	<ul className="mb-0 mt-1">
																		{result.matches.map((match, idx) => (
																			<li key={idx}>
																				<code>{match}</code>
																			</li>
																		))}
																	</ul>
																</div>
															</>
														) : (
															<div className="mt-1">⚠ Nenhum match encontrado</div>
														)
													) : (
														<div className="mt-1">✗ {result.error}</div>
													)}
												</Alert>
											);
										})}
									</div>
								)}
							</>
						)}

						<Row>
							<Form.Group className="mb-3 col-md-6" controlId="removeAssetsFromPayloadResponse">
								<Form.Label>Remover Assets do Payload da Resposta</Form.Label>
								<Form.Check
									type="switch"
									checked={formRemoveAssetsFromPayloadResponse}
									onChange={() => {
										setFormRemoveAssetsFromPayloadResponse(!formRemoveAssetsFromPayloadResponse);
									}}
									disabled={!formStatus}
								/>
							</Form.Group>
							{id && (
								<Form.Group className="mb-3 col-md-6" controlId="ativo">
									<Form.Label>Ativo</Form.Label>
									<Form.Check
										type="switch"
										checked={formAtivo}
										onChange={() => {
											setFormAtivo(!formAtivo);
										}}
										disabled={!formStatus}
									/>
								</Form.Group>
							)}
						</Row>
					</Card.Body>
				</Card>

				<div className="d-flex gap-2 mb-4">
					{!formStatus && (
						<Button
							variant="dark"
							className="rounded-3 shadow-sm"
							onClick={() => {
								setFormStatus(true);
							}}
						>
							Editar Informações
						</Button>
					)}
					{formStatus && (
						<>
							<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
								{formSaving ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" /> Salvando
									</>
								) : (
									"Salvar Informações"
								)}
							</Button>
							<Button
								variant="light"
								className="rounded-3 shadow-sm"
								type="button"
								onClick={() => {
									if (id) {
										setFormStatus(false);
									} else {
										navigate("/monitores");
									}
								}}
								disabled={formSaving}
							>
								Cancelar
							</Button>
						</>
					)}
					<Button
						variant="outline-dark"
						type="button"
						onClick={() => {
							setCurlModal(true);
						}}
						disabled={formSaving}
					>
						cURL
					</Button>
				</div>
			</Form>
			{!formStatus && data?.Data && (
				<>
					<Card className="mb-4">
						<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">Status</Card.Header>
						<Card.Body>
							<Row>
								<Col md={4}>
									<div className="fw-lighter">Saúde</div>
									<div className="fw-light">
										<Icon path={mdiPulse} size={1} className={`me-1 ${iconClass}`} /> {saudeDescricao}
									</div>
								</Col>
								<Col md={4}>
									<div className="fw-lighter">Latência</div>
									<div className="fw-light">{data?.Data?.ultimaLatencia ?? "..."}</div>
								</Col>
								<Col md={4}>
									<div className="fw-lighter">Data Hora Checagem</div>
									<div className="fw-light">{data?.Data?.dataUltimaChecagem ?? "..."}</div>
								</Col>
							</Row>
						</Card.Body>
					</Card>

					<Tab.Container
						activeKey={tab}
						onSelect={(key) => {
							if (key) setTab(key);
						}}
					>
						<Nav variant="tabs" className="fs-5 fw-light mb-2">
							<Nav.Item>
								<Nav.Link eventKey="tarefas">
									<Icon path={mdiCalendarCheckOutline} size={1} className="me-2" /> Tarefas
								</Nav.Link>
							</Nav.Item>
							<Nav.Item>
								<Nav.Link eventKey="alertas">
									<Icon path={mdiBellOutline} size={1} className="me-2" /> Alertas Associados
								</Nav.Link>
							</Nav.Item>
						</Nav>
						<Tab.Content className="mb-4">
							<Tab.Pane eventKey="tarefas">
								{tarefas ? (
									<>
										{queryTarefas.isLoading &&
											Array.from({ length: Number(SkeletonCount) }, (_, index) => {
												return <TarefasCard skeleton />;
											})}
										{!queryTarefas.isLoading && queryTarefas.isError && (
											<Alert variant="secondary" className="text-center">
												Problema na operação. Tente novamente mais tarde
											</Alert>
										)}
										{!queryTarefas.isLoading && queryTarefas.data?.pages[0].Data && queryTarefas.data.pages[0].Data.length === 0 && (
											<Alert variant="light" className="text-center p-4 d-flex justify-content-center align-items-center">
												<Icon path={mdiCardRemoveOutline} size={1} className="me-2" />
												Nenhum registro encontrado
											</Alert>
										)}
										{!queryTarefas.isLoading &&
											queryTarefas.data?.pages.map((dataPage) => {
												return (
													dataPage?.Data &&
													dataPage?.Data.length >= 1 &&
													dataPage?.Data.map((item) => {
														return (
															<TarefasCard
																key={item.id}
																data={item}
																onClick={() => {
																	setTarefa(item.id);
																	setTarefaModal(true);
																}}
															/>
														);
													})
												);
											})}
										<Col md={12} className="text-center">
											<InfiniteScroll
												more={queryTarefas.hasNextPage}
												loading={queryTarefas.isFetchingNextPage}
												loader={<TarefasCard skeleton />}
												load={() => {
													queryTarefas.fetchNextPage();
												}}
											/>
										</Col>
									</>
								) : (
									<>
										{data?.Data?.tasks && data.Data.tasks.length > 0 ? (
											data.Data.tasks.map((item, index) => (
												<TarefasCard
													key={index}
													data={item}
													onClick={() => {
														setTarefa(item.id);
														setTarefaModal(true);
													}}
												/>
											))
										) : (
											<Alert variant="light" className="text-center">
												Nenhuma tarefa para este monitor.
											</Alert>
										)}

										<Button
											variant="light"
											onClick={() => {
												setTarefas(true);
											}}
										>
											Carregar mais
										</Button>
									</>
								)}
							</Tab.Pane>
							<Tab.Pane eventKey="alertas">
								{data?.Data?.alertas && data.Data.alertas.length > 0 ? (
									data.Data.alertas.map((item: any, index: number) => <AlertasCard key={index} data={item} />)
								) : (
									<Alert variant="light" className="text-center">
										Nenhum alerta cadastrado para este monitor.
									</Alert>
								)}
							</Tab.Pane>
						</Tab.Content>
					</Tab.Container>
				</>
			)}

			<Modal
				show={curlModal}
				onHide={() => {
					setCurlModal(false);
					setCurlCommand("");
				}}
				size="lg"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>cURL</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form.Control as="textarea" rows={10} value={curlCommand} onChange={(e) => setCurlCommand(e.target.value)} />
				</Modal.Body>
				<Modal.Footer>
					<Button variant="dark" onClick={handleExportCurl} disabled={!id}>
						Exportar cURL
					</Button>
					<Button
						onClick={() => {
							const parsedData = parseCurlCommand(curlCommand);
							if (parsedData) {
								setFormMetodo(parsedData.method || "");
								setFormUrl(parsedData.url || "");
								setFormHeaders(parsedData.headers || []);
								setFormPayload(parsedData.payload || "");
								setFormPayloadType(parsedData.payloadType || "");
								if (parsedData.payloadFields && parsedData.payloadFields.length > 0) {
									setFormPayloadFields(parsedData.payloadFields);
								} else {
									setFormPayloadFields([]);
								}
							}
							setCurlModal(false);
						}}
						disabled={!formStatus}
					>
						Importar
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal
				show={tarefaModal}
				onHide={() => {
					setTarefaModal(false);
				}}
				size="xl"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>Tarefa</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{queryTarefa.isLoading && <Spinner animation="border" />}
					{queryTarefa.data?.Data && <TarefaCard data={queryTarefa.data.Data} />}
				</Modal.Body>
				<Modal.Footer>
					<Button onClick={handlePDF} disabled={queryTarefa.isLoading || !queryTarefa.data?.Data}>
						{queryTarefa.isLoading ? <Spinner animation="border" size="sm" /> : "Gerar PDF"}
					</Button>
				</Modal.Footer>
			</Modal>
		</Layout>
	);
}
